import { PinCreateProperties, tracker } from '@lessonup/analytics';
import { PinViewerStatic, UpdatePinDispatch } from '@lessonup/pin-renderer';
import {
  FillInTheBlanksPinLayoutTemplate,
  newAnswerByCategoryPin,
  newDragAndDropPin,
  newFillInTheBlanksPin,
  newOpenQuestionPin,
  newPin,
  newPollPin,
  newQuizPin,
  newSlidePin,
  newSortingQuestionPin,
  newWordCloudPin,
  OpenQuestionPinLayoutTemplate,
  PinCreationParams,
  PinTheme,
  PollPinTypeValues,
  QuizPinLayoutTemplate,
} from '@lessonup/pins-shared';
import {
  BodyText,
  color,
  createModal,
  Grid,
  ManagedModal,
  ModalPopover,
  ModalPopoverAnchor,
  ModalPopoverDirection,
  NiceModalHandler,
  SpaceBetween,
  spacing,
  styled,
  Thumb,
  useModal,
} from '@lessonup/ui-components';
import { first } from 'lodash';
import React from 'react';
import { useFeatureFlags } from '../../../../utils/featureFlags/useFeatureFlags';
import { useEditorTranslation } from '../../hooks/useEditorTranslation';
import { createTranslatedTemplate } from '../../utils/createTranslatedTemplate';
import { isLearningPhaseOrder, LearningPhaseOrder } from '../../utils/learningPhases/LearningPhase';
import { generateMaiaQuiz } from '../../utils/maia.utils';

interface ModalProps {
  lessonId: string;
  dispatch: UpdatePinDispatch; // shortcut: got the error 'no dispatch for updatePinContext' when using useUpdatePin inside the modal. Since this is a temp solution, not needed to fix that challenge for now.
  setActivePinId: (pinId: string) => void;
  activePinId?: string | null;
  addBehindPinOrPhaseNumber: string | LearningPhaseOrder;
  pinTheme: PinTheme;
}

export const TempAddPinModal = createModal(
  ({ lessonId, dispatch, setActivePinId, addBehindPinOrPhaseNumber, pinTheme }: ModalProps) => {
    const modal = useModal();
    return (
      <ManagedModal modal={modal} contentLabel="temp" width="520px">
        <TempAddPinModalBody
          lessonId={lessonId}
          modal={modal}
          dispatch={dispatch}
          setActivePinId={setActivePinId}
          addBehindPinOrPhaseNumber={addBehindPinOrPhaseNumber}
          pinTheme={pinTheme}
        />
      </ManagedModal>
    );
  }
);

interface ModalPopoverProps {
  lessonId: string;
  direction: ModalPopoverDirection;
  modalAnchor: ModalPopoverAnchor;
  parentSelector: () => HTMLLIElement | HTMLButtonElement | null;
  dispatch: UpdatePinDispatch; // shortcut: got the error 'no dispatch for updatePinContext' when using useUpdatePin inside the modal. Since this is a temp solution, not needed to fix that challenge for now.
  setActivePinId: (pinId: string) => void;
  addBehindPinOrPhaseNumber: string | LearningPhaseOrder;
  pinTheme?: PinTheme;
}

export const TempAddPinModalPopover = createModal(
  ({
    lessonId,
    direction,
    modalAnchor,
    parentSelector,
    dispatch,
    setActivePinId,
    addBehindPinOrPhaseNumber,
    pinTheme,
  }: ModalPopoverProps) => {
    const modal = useModal();

    return (
      <ModalPopover
        direction={direction}
        modalAnchor={modalAnchor}
        parentSelector={parentSelector}
        modal={modal}
        contentLabel="temp"
        preventScrollOutside={false}
      >
        <TempAddPinModalBody
          lessonId={lessonId}
          modal={modal}
          dispatch={dispatch}
          setActivePinId={setActivePinId}
          addBehindPinOrPhaseNumber={addBehindPinOrPhaseNumber}
          pinTheme={pinTheme}
        />
      </ModalPopover>
    );
  }
);

interface TempAddPinModalBodyProps {
  modal: NiceModalHandler<Record<string, unknown>>;
  dispatch: UpdatePinDispatch;
  setActivePinId: (pinId: string) => void;
  addBehindPinOrPhaseNumber: string | LearningPhaseOrder;
  lessonId: string;
  pinTheme?: PinTheme;
}

const PIN_WIDTH = 152;

export const TempAddPinModalBody = ({
  modal,
  dispatch,
  setActivePinId,
  addBehindPinOrPhaseNumber,
  lessonId,
  pinTheme,
}: TempAddPinModalBodyProps) => {
  const { t } = useEditorTranslation();
  const template = createTranslatedTemplate();
  const phaseNumber = isLearningPhaseOrder(addBehindPinOrPhaseNumber) ? addBehindPinOrPhaseNumber : undefined;
  const afterPinId = !isLearningPhaseOrder(addBehindPinOrPhaseNumber) ? addBehindPinOrPhaseNumber : undefined;
  const onAddNewPins = (pinIds: string[]) => {
    setActivePinId(pinIds[pinIds.length - 1]);
  };
  const { isFeatureEnabled } = useFeatureFlags();

  const startGenerateMaiaQuiz = (layout: QuizPinLayoutTemplate) => {
    generateMaiaQuiz(afterPinId, layout, dispatch, onAddNewPins, lessonId);
    modal.hide();
  };

  const maiaQuizQuestionTemplateLayout = first(template.layouts.quizPinLayouts);
  const maiaQuizQuestionLayoutPin =
    maiaQuizQuestionTemplateLayout && newPin(newQuizPin(maiaQuizQuestionTemplateLayout));

  const openQuestionTemplateLayouts: OpenQuestionPinLayoutTemplate['key'][] = ['TITLE_ONLY', 'IMAGE_LEFT'];

  const wordCloudTemplateLayout = first(template.layouts.wordCloudLayouts);
  const wordCloudLayoutPin = wordCloudTemplateLayout && newPin(newWordCloudPin(wordCloudTemplateLayout));

  const fillInTheBlankTemplateLayout = first(template.layouts.fillInTheBlanksLayouts);
  const fillInTheBlankLayoutPin =
    fillInTheBlankTemplateLayout && newPin(newFillInTheBlanksPin(fillInTheBlankTemplateLayout));

  const addFillInTheBlank = (layout: FillInTheBlanksPinLayoutTemplate) => {
    dispatch({
      type: 'addPins',
      endOfPhase: phaseNumber,
      afterPinId: afterPinId,
      pins: [newFillInTheBlanksPin(layout)],
      pinTheme,
      onComplete: onAddNewPins,
    });
    trackPinCreate('fill_in_the_blanks');
    modal.hide();
  };

  const addPin = (pin: PinCreationParams, trackEvent: PinCreateProperties['pinType']) => {
    dispatch({
      type: 'addPins',
      endOfPhase: phaseNumber,
      afterPinId: afterPinId,
      pins: [pin],
      pinTheme,
      onComplete: (pinIds) => onAddNewPins(pinIds),
    });
    trackPinCreate(trackEvent);

    tracker.events.themeApplied({
      lessonId,
      themeId: pinTheme?.themeId ?? '',
      themeSettingResetOverwrites: false,
      themeSettingApplyToAll: false,
      themeVersion: pinTheme?.id ?? '',
      pinCount: 1,
    });
    modal.hide();
  };

  function trackPinCreate(pinType: PinCreateProperties['pinType']) {
    tracker.events.pinCreate({
      lessonId,
      pinType,
      pinCreationMethod: 'create_own',
      editorVersion: 'v2',
    });
  }

  return (
    <StyledModalContent direction="y" spacing={spacing.size16}>
      <BodyText size="medium" weight="bold">
        {t('layouts.informative')}
      </BodyText>

      <StyledLayoutList cols={3} gap={spacing.size16} as="ul">
        {template.layouts.slidePinLayouts.map((layout, index) => {
          if (layout.key === 'VIDEO_ONLY' && !isFeatureEnabled('editorVideoPinComponent')) {
            return;
          }
          const pin = newPin(newSlidePin(layout));

          return (
            <li key={`slide-${layout.key} ${index}`}>
              <Thumb
                thumbType="caption"
                number={index + 1}
                onClick={() => addPin(newSlidePin(layout), 'slide')}
                caption={t(`layouts.slide.${layout.key}`)}
              >
                <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
              </Thumb>
            </li>
          );
        })}
      </StyledLayoutList>

      <BodyText size="medium" weight="bold">
        {t('layouts.interactive')}
      </BodyText>
      <StyledLayoutList cols={3} gap={spacing.size16} as="ul">
        {template.layouts.quizPinLayouts.map((layout, index) => {
          const pin = newPin(newQuizPin(layout));

          return (
            <li key={`quiz-${layout.key} ${index}`}>
              <Thumb
                thumbType="caption"
                number={index + 1}
                onClick={() => addPin(newQuizPin(layout), 'quiz')}
                caption={`${t(`layouts.quiz.${layout.key}`)}`}
              >
                <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
              </Thumb>
            </li>
          );
        })}
        {maiaQuizQuestionLayoutPin && (
          <li key={`maia-quiz-${maiaQuizQuestionTemplateLayout.key}`}>
            <Thumb
              thumbType="caption"
              number={1}
              onClick={() => startGenerateMaiaQuiz(maiaQuizQuestionTemplateLayout)}
              caption={`${t('layouts.quiz.generateMaiaQuiz')}`}
            >
              <PinViewerStatic pin={maiaQuizQuestionLayoutPin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
            </Thumb>
          </li>
        )}
        {template.layouts.openQuestionLayouts
          .filter(({ key }) => openQuestionTemplateLayouts.includes(key))
          .map((layout, index) => {
            const pin = newPin(newOpenQuestionPin(layout));

            return (
              <li key={`open-question-${layout.key} ${index}`}>
                <Thumb
                  thumbType="caption"
                  number={index + 1}
                  onClick={() => addPin(newOpenQuestionPin(layout), 'open_question')}
                  caption={`${t(`layouts.openQuestion.${layout.key}`)}`}
                >
                  <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
                </Thumb>
              </li>
            );
          })}
        {wordCloudLayoutPin && (
          <li key={`word-cloud-${wordCloudTemplateLayout.key}`}>
            <Thumb
              thumbType="caption"
              number={1}
              onClick={() => addPin(newWordCloudPin(wordCloudTemplateLayout), 'mind_map')}
              caption={`${t(`layouts.wordCloud.${wordCloudTemplateLayout.key}`)}`}
            >
              <PinViewerStatic pin={wordCloudLayoutPin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
            </Thumb>
          </li>
        )}
        {isFeatureEnabled('fillInTheBlanksPin') && fillInTheBlankLayoutPin && (
          <li key={`fill-in-the-blank-${fillInTheBlankTemplateLayout.key}`}>
            <Thumb
              thumbType="caption"
              number={1}
              onClick={() => addFillInTheBlank(fillInTheBlankTemplateLayout)}
              caption={`${t(`layouts.fillInTheBlank.${fillInTheBlankTemplateLayout.key}`)}`}
            >
              <PinViewerStatic pin={fillInTheBlankLayoutPin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
            </Thumb>
          </li>
        )}
        {isFeatureEnabled('editorPollPinV2') &&
          PollPinTypeValues.flatMap((pollType) =>
            (template.layouts.pollPinLayouts || []).map((layout) => ({ layout, pollType }))
          )
            .filter(({ pollType }) => pollType === 'EMOJI' || isFeatureEnabled('editorPollTextPinV2'))
            .map(({ layout, pollType }, index) => {
              const pin = newPin(newPollPin(layout, pollType));
              return (
                <li key={`poll-${layout.key}-${pollType}`}>
                  <Thumb
                    thumbType="caption"
                    number={index + 1}
                    onClick={() => addPin(pin, pollType === 'EMOJI' ? 'poll_emoji' : 'poll_text')}
                    caption={t(`layouts.poll.${layout.key}`, { type: t(`poll.type.${pollType}`) })}
                  >
                    <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
                  </Thumb>
                </li>
              );
            })}
        {isFeatureEnabled('editorSortingPin') &&
          template.layouts.sortingQuestionLayouts.map((layout, index) => {
            const pin = newPin(newSortingQuestionPin(layout));

            return (
              <li key={`sorting-question-${layout.key} ${index}`}>
                <Thumb
                  thumbType="caption"
                  number={index + 1}
                  onClick={() => addPin(newSortingQuestionPin(layout), 'sorting_question')}
                  caption={`${t(`layouts.sortingQuestion.${layout.key}`)}`}
                >
                  <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
                </Thumb>
              </li>
            );
          })}
        {isFeatureEnabled('editorAnswerByCategoryPin') &&
          template.layouts.answerByCategoryLayouts.map((layout, index) => {
            const pin = newPin(newAnswerByCategoryPin(layout));
            return (
              <li key={`answer-by-category-${layout.key} ${index}`}>
                <Thumb
                  thumbType="caption"
                  number={index + 1}
                  onClick={() => addPin(newAnswerByCategoryPin(layout), 'category_question')}
                  caption={`${t(`layouts.answerByCategory.${layout.key}`)}`}
                >
                  <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
                </Thumb>
              </li>
            );
          })}

        {isFeatureEnabled('editorDragAndDropPin') &&
          template.layouts.dragAndDropLayouts
            .filter((layout) => layout.key === 'SINGLE_DROP_ZONE')
            .map((layout, index) => {
              const pin = newPin(newDragAndDropPin(layout));
              return (
                <li key={`drag-and-drop-${layout.key} ${index}`}>
                  <Thumb
                    thumbType="caption"
                    number={index + 1}
                    onClick={() => addPin(newDragAndDropPin(layout), 'drag_and_drop')}
                    caption={`${t(`layouts.dragAndDrop.${layout.key}`)}`}
                  >
                    <PinViewerStatic pin={pin} width={PIN_WIDTH} isThumb pinTheme={pinTheme} />
                  </Thumb>
                </li>
              );
            })}
      </StyledLayoutList>
    </StyledModalContent>
  );
};

const StyledModalContent = styled(SpaceBetween)`
  background: ${color.neutral.surface1.background};
  padding: ${spacing.size16};
`;

const StyledLayoutList = styled(Grid)`
  margin: 0;
  padding: 0;
  list-style: none;
`;
